import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

import hooks from '@g4/automation-hooks';

import { Box, Text, ConditionalWrap } from '../Boneyard';

export const propTypes = {
  /** Input field `name` attribute value */
  name: PropTypes.string,
  /** The value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The defaut value */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * HTML input type
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types
   */
  type: PropTypes.string.isRequired,
  /** Text to display whilst a value hasn't been selected */
  placeholder: PropTypes.string,
  /** Display a border around the input */
  withBorder: PropTypes.bool,
  /** Prevents text from being entered */
  disabled: PropTypes.bool,
  /** Applies error styling */
  hasError: PropTypes.bool,
  /**
   * Hides the spin button controls for numeric input fields
   *
   * MDN Warning: This feature is non-standard and is not on a standards track.
   * Do not use it on production sites facing the Web: it will not work for every user.
   */
  hideSpinControls: PropTypes.bool,
  /** A ref to the <input> element */
  inputRef: PropTypes.object,
  /** onchange callback handler */
  onChange: PropTypes.func,
  /** onblur challback handler */
  onBlur: PropTypes.func,
  /** onfocus challback handler */
  onFocus: PropTypes.func,
  /** The maximum number of characters allowed to be entered */
  maxLength: PropTypes.number,
  /** Prevents users from modifying the field value */
  readOnly: PropTypes.bool,
  /** A regular expression the input's contents must match in order to be valid */
  pattern: PropTypes.string,
  /**
   * HTML `autocomplete` attribute
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete: PropTypes.string,
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  errorId: PropTypes.string,
  automationHook: PropTypes.string,
  theme: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
  ariaDescribedBy: PropTypes.string,
  prefix: PropTypes.string,
  /** Adds a prefix for the input field */
};

export const defaultProps = {
  withBorder: true,
  onChange: Function.prototype,
  onBlur: Function.prototype,
  hasError: false,
  prefix: '',
};

const ClickableWrapper = styled.div`
  cursor: ${props => (props.disabled ? 'initial' : 'pointer')};
`;

const InputWrapper = styled(Box)`
  display: flex;
  height: 50px;
  align-items: center;
  max-width: 300px;
  background: #fff;
  overflow: hidden;
  padding: 0 20px;
  border-radius: 8px;
  text-overflow: ellipsis;
  border: 1px solid #d6d6d6;

  &:hover,
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.input.focused};
  }

  > input {
    flex-grow: 1;
    font-size: 14px;
    background: #fff;
    border: none;
    outline: none;
    padding: 0.5rem;
    padding-left: 0.2rem;

    &:focus-within {
      border-color: none;
      outline: none;
      box-shadow: none;
    }

    &:hover {
      box-shadow: none;
    }
  }
`;

// prettier-ignore
const Input = styled(withTheme(props => (
  <Box position="relative">
    <ConditionalWrap
      condition = {props.onClick}
      wrap={ children => <ClickableWrapper onClick={props.onClick} disabled={props.disabled}>{children}</ClickableWrapper> }
    >
    <ConditionalWrap
      condition = {props.prefix}
      wrap={ children => <InputWrapper><span><Text size={14} color={props.value ? 'inherit' : props.theme.colors.input.disabledText}>{props.prefix}</Text></span>{children}</InputWrapper> }
    >
      <input
        ref={props.inputRef}
        name={props.name}
        type={props.type}
        id={props.id}
        value={props.value}
        defaultValue={props.defaultValue}
        placeholder={props.placeholder}
        disabled={props.disabled}
        aria-describedby={props.hasError? props.errorId : undefined}
        aria-invalid={props.hasError}
        maxLength={props.maxLength}
        pattern={props.pattern}
        readOnly={props.readOnly}
        autoComplete={props.autoComplete}
        onChange={event => props.onChange(event.target.value)}
        onBlur={event => props.onBlur(event.target.value)}
        onFocus={props.onFocus}
        className={props.className}
        {...hooks.hook(props.automationHook)}
        tabIndex={props.onClick ? "-1" : "0"}
      />
    </ConditionalWrap>
    </ConditionalWrap>

    {props.children}
  </Box>
)))`
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 ${props => props.theme.spacing(4)};
  border-radius: 8px;

  ${props => props.disabled && `
    color: ${props.theme.colors.input.disabledText};
    background-color: ${props.theme.colors.greyA100};
  `}

  &:hover,
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.disabled ? props.theme.colors.input.disabledBackground : props.theme.colors.input.focused};
  }

  ${props => props.icon && `
    /* avoid collision with icon */
    padding-right: 50px;
  `}

  ${props => props.withBorder && `
    border: 1px solid ${props.theme.colors.input.border};
  `}


  ${props => props.hasError && !props.disabled && `
    border-color: ${props.theme.colors.input.error};
  `}

  ${props => props.hideSpinControls && `
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  `}

  &::placeholder {
    color: ${props => props.theme.colors.input.placeholderText};
  }
`;

Input.displayName = 'BoneyardInput';
Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

export default Input;
